import { useState, useEffect } from 'react';
import { Loader2, Link2Off, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { useDeviceLinking, type DeviceType } from '@/hooks/useDeviceLinking';
import { authenticatedFetch } from '@/lib/api';

interface UnitPickerDrawerProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  deviceName: string;
  deviceExternalId?: string | null;
  deviceType: DeviceType;
  currentUnitId: string | null;
  currentUnitCode: string | null;
  /**
   * Other unlinked devices naming the same unit. A trailer's Starlink and
   * Sol-Ark are discovered together and belong together, so linking them one
   * drawer at a time is busywork that leaves half the job undone.
   */
  alsoLinkable?: LinkableDevice[];
  onUpdated: () => void;
}

export interface LinkableDevice {
  id: string;
  type: DeviceType;
  /** What the serial IS — 'Service line', 'Inverter S/N', … */
  serialLabel: string;
  serial: string;
}

type LinkedDeviceCounts = Partial<Record<DeviceType, number>>;

interface UnitOption {
  id: string;
  unit_code: string;
  model_code: string | null;
  /** How many devices of each type this unit already holds. */
  linked_devices: LinkedDeviceCounts;
}

const DEVICE_LABELS: Record<DeviceType, string> = {
  starlink: 'Starlink',
  solark: 'Sol-Ark',
  omni: 'GPS',
  tesla: 'Tesla',
  hvac: 'HVAC',
};

/**
 * What the device's external id IS, per type. Calling a Starlink service line
 * a "Plant ID" — Sol-Ark's word — is how this drawer used to describe every
 * device it opened on.
 */
const EXTERNAL_ID_LABELS: Record<DeviceType, string> = {
  starlink: 'Service line',
  solark: 'Plant ID',
  omni: 'Device ID',
  tesla: 'Charger ID',
  hvac: 'Device ID',
};

interface CompanyOption {
  id: string;
  name: string;
}

/**
 * The model code implied by a unit code's leading token — codes read
 * {model}{sequence}TCA, so 'WEA002TCA' implies WEA.
 *
 * This is a GUESS, so it is only used when `known` confirms the code exists in
 * filter_models. Sending an unconfirmed guess made the server reject the whole
 * creation, which blocked onboarding a genuinely new hardware type from the
 * UI: the operator could not create the unit at all until someone added the
 * model. Now an unrecognised prefix simply yields no model code — the unit is
 * created, and the model can be attached once it properly exists.
 */
function deriveModelCode(unitCode: string, known: Set<string>): string | null {
  const match = /^([A-Z0-9]{3})\d+[A-Z]*$/.exec(unitCode.trim().toUpperCase());
  return match && known.has(match[1]) ? match[1] : null;
}

export function UnitPickerDrawer({
  open,
  onClose,
  deviceId,
  deviceName,
  deviceExternalId,
  deviceType,
  currentUnitId,
  currentUnitCode,
  alsoLinkable = [],
  onUpdated,
}: UnitPickerDrawerProps) {
  const { linkDevice, unlinkDevice, isSubmitting } = useDeviceLinking();
  // Devices are named after the unit they belong to (a Sol-Ark plant name IS
  // the unit code; Starlink SSIDs follow the same convention), so seeding the
  // search with it puts the right unit — or the offer to create it — on screen
  // with zero typing.
  const [search, setSearch] = useState(() => (currentUnitId ? '' : deviceName === '—' ? '' : deviceName));
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<UnitOption | null>(null);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  // The real model codes, so a derived prefix is confirmed before it is sent.
  const [knownModels, setKnownModels] = useState<Set<string>>(new Set());
  const [companyId, setCompanyId] = useState<string>('');
  const [resolvedFor, setResolvedFor] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  // When the device names a unit that does not exist, creating it IS the
  // answer — the search is the exception, so it stays folded away until asked
  // for rather than making every onboarding start with a fruitless query.
  const [showSearch, setShowSearch] = useState(false);
  // Siblings are matched by DISPLAY NAME upstream, and a display name is not a
  // unique key — two distinct Sol-Ark plants can both read 2RE012TCA. Checking
  // them by default would wire the wrong plant to a unit with one click and no
  // way to notice. The match is a suggestion; the operator confirms it.
  const [alsoSelected, setAlsoSelected] = useState<Record<string, boolean>>({});

  // Search units when query changes
  useEffect(() => {
    if (!open) return;
    setSelectedUnit(null);

    const searchUnits = async () => {
      setIsSearching(true);
      try {
        // ALWAYS /units/search. The old short-query branch fell back to
        // /admin/units, which ignores `limit`, returns the whole fleet, and
        // carries no linked_devices — so targetOccupied read false and a real
        // replacement was sent without `force`, surfacing as a raw 409.
        // Under two characters that endpoint answers an empty list by design,
        // which is the honest result for a query too short to disambiguate.
        const query = search.trim();
        if (query.length < 2) {
          setUnits([]);
          setResolvedFor(search);
          setIsSearching(false);
          return;
        }

        const params = new URLSearchParams({ limit: '30', q: query });
        const response = await authenticatedFetch(`/api/v1/admin/units/search?${params}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const unitList = (data.units || data || []).map((u: Record<string, unknown>) => ({
          id: u.id as string,
          unit_code: u.unit_code as string,
          model_code: (u.model_code as string) || null,
          linked_devices: (u.linked_devices as LinkedDeviceCounts) || {},
        }));
        setUnits(unitList);
        setResolvedFor(search);
      } catch {
        setUnits([]);
        setResolvedFor(search);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchUnits, 200);
    return () => clearTimeout(debounce);
  }, [open, search]);

  // A drawer reused for a different device must not inherit the previous
  // one's selection, reason text or error — those belong to the device that
  // was open, and carrying them over would attribute one device's swap reason
  // to another.
  useEffect(() => {
    setSelectedUnit(null);
    setAlsoSelected({});
    setReason('');
    setLinkError(null);
    setCreateError(null);
    setShowSearch(false);
    setSearch(currentUnitId ? '' : deviceName === '—' ? '' : deviceName);
  }, [deviceId, currentUnitId, deviceName]);

  // Companies to own a newly created unit. Loaded once the drawer opens so the
  // create path is ready the moment a search comes back empty.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch('/api/v1/admin/companies');
        if (!response.ok) return;
        const data = await response.json();
        const list: CompanyOption[] = (data.companies || []).map((c: Record<string, unknown>) => ({
          id: c.id as string,
          name: (c.name as string) || (c.slug as string) || 'Unnamed',
        }));
        if (cancelled) return;
        setCompanies(list);
        setCompanyId(prev => prev || list[0]?.id || '');
      } catch {
        if (!cancelled) setCompanies([]);
      }

      try {
        const response = await authenticatedFetch('/api/v1/admin/filters/models');
        if (!response.ok) return;
        const data = await response.json();
        const list = Array.isArray(data) ? data : data.models || [];
        if (!cancelled) {
          setKnownModels(new Set(list.map((m: Record<string, unknown>) => String(m.code))));
        }
      } catch {
        // An empty set means no model code is sent — the unit still gets
        // created, which is the safe direction to fail.
        if (!cancelled) setKnownModels(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const searchCode = search.trim().toUpperCase();

  /**
   * The unit this device is going to. Resolution order: an explicit pick from
   * the search beats the code the device names itself after. `null` with a
   * usable searchCode means the unit does not exist and must be created.
   *
   * `resolvedFor` is what `units` actually answers for — without it the target
   * flips mid-debounce against a stale list.
   */
  const settled = !isSearching && resolvedFor === search;
  const exactMatch = units.find(u => u.unit_code.toUpperCase() === searchCode) ?? null;
  const targetUnit = selectedUnit ?? exactMatch;
  const derivedModel = deriveModelCode(searchCode, knownModels);
  const willCreate = settled && !targetUnit && searchCode.length >= 3 && derivedModel !== null;

  // Linking only displaces something when the target already holds this type of
  // device, or this device is wired to another unit today. Those force, and
  // forcing needs a reason the audit row can carry.
  const targetOccupied = (targetUnit?.linked_devices?.[deviceType] ?? 0) > 0;
  const isSwap = Boolean(targetUnit) && (targetOccupied || Boolean(currentUnitId));

  const extraToLink = alsoLinkable.filter(d => alsoSelected[d.id]);
  const linkCount = 1 + extraToLink.length;

  const primaryLabel = willCreate
    ? `Create unit & link ${linkCount === 1 ? 'device' : `${linkCount} devices`}`
    : !targetUnit
      ? 'Select a unit'
      : isSwap
        ? `Replace & link ${linkCount === 1 ? 'device' : `${linkCount} devices`}`
        : `Link ${linkCount === 1 ? 'device' : `${linkCount} devices`}`;

  /**
   * Link this device plus every checked sibling to one unit.
   *
   * A sibling failure is NOT a success. Dropping it silently leaves a device
   * unlinked while the drawer closes as if the job were done, and nobody goes
   * looking for it. The primary having succeeded is real, so it is not undone —
   * the drawer stays open naming what still needs linking.
   */
  const linkAll = async (unitId: string, force: boolean, why?: string) => {
    const primary = await linkDevice(unitId, deviceType, deviceId, force, why);
    if (!primary.success) return false;

    const failed: string[] = [];
    // Siblings go into empty slots on the same unit, so they never force.
    for (const extra of extraToLink) {
      const result = await linkDevice(unitId, extra.type, extra.id, false);
      if (!result.success) failed.push(`${DEVICE_LABELS[extra.type]} ${extra.serial}`);
    }

    if (failed.length > 0) {
      setLinkError(
        `Linked ${deviceName}, but ${failed.join(' and ')} could not be linked. Retry from the unlinked list.`,
      );
      return false;
    }
    return true;
  };

  /**
   * The drawer's single action. What it does is stated on the button, so the
   * create-vs-link decision is already made by the time anyone clicks.
   */
  const handlePrimary = async () => {
    setLinkError(null);
    setCreateError(null);

    if (targetUnit) {
      if (isSwap && !reason.trim()) {
        setLinkError('Enter a reason for replacing the existing link.');
        return;
      }
      if (await linkAll(targetUnit.id, isSwap, isSwap ? reason.trim() : undefined)) {
        onUpdated();
        onClose();
      }
      return;
    }

    if (!willCreate || !companyId) return;

    setIsCreating(true);
    try {
      const response = await authenticatedFetch('/api/v1/admin/units', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          unit_code: searchCode,
          company_id: companyId,
          model_code: deriveModelCode(searchCode, knownModels),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setCreateError(data.message || `Could not create unit (${response.status})`);
        return;
      }
      const created = data.unit || data;
      if (!created?.id) {
        setCreateError('Unit was created but the response carried no id.');
        return;
      }
      // A unit created a moment ago holds nothing, so nothing is displaced.
      if (await linkAll(created.id, false)) {
        onUpdated();
        onClose();
      }
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Could not create unit');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUnlink = async () => {
    if (!currentUnitId) return;
    if (!confirm(`Unlink ${deviceName} from ${currentUnitCode}?`)) return;
    // This drawer is always scoped to one specific device, so name it — the unit
    // may hold a second inverter and the backend will not guess between them.
    const success = await unlinkDevice(currentUnitId, deviceType, deviceId);
    if (success) {
      onUpdated();
      onClose();
    }
  };

  return (
    <AdminFormDrawer
      open={open}
      onClose={onClose}
      title={`${currentUnitId ? 'Manage' : 'Link'} ${DEVICE_LABELS[deviceType]}: ${deviceName}`}
      description={currentUnitId
        ? `Currently linked to ${currentUnitCode}`
        : `Choose the unit this ${DEVICE_LABELS[deviceType]} device belongs to`}
    >
      <div className="space-y-5">
        {deviceExternalId && (
          <div className="flex items-baseline gap-3 text-xs">
            <span className="uppercase tracking-wide text-muted-foreground/60 shrink-0">
              {EXTERNAL_ID_LABELS[deviceType]}
            </span>
            <span className="font-mono text-muted-foreground break-all">{deviceExternalId}</span>
          </div>
        )}

        {currentUnitId && (
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2 text-red-400 border-red-400/20 hover:bg-red-400/10"
            onClick={handleUnlink}
            disabled={isSubmitting}
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2Off className="h-4 w-4" />}
            Unlink from {currentUnitCode}
          </Button>
        )}

        {/* The resolved destination — one card, never competing with a list. */}
        <section className="space-y-2">
          <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
            {currentUnitId ? 'Reassign to' : 'Link to'}
          </h3>

          {!settled ? (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-5 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Resolving unit…
            </div>
          ) : targetUnit ? (
            <div className="rounded-lg border border-border bg-card px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-mono text-base font-semibold">{targetUnit.unit_code}</span>
                {targetUnit.model_code && (
                  <span className="text-xs text-muted-foreground">{targetUnit.model_code}</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {(Object.keys(DEVICE_LABELS) as DeviceType[]).map(type => {
                  const count = targetUnit.linked_devices?.[type] ?? 0;
                  if (!count) return null;
                  return (
                    <span
                      key={type}
                      className={cn(
                        'text-[10px] px-1.5 py-0.5 rounded border',
                        type === deviceType
                          ? 'border-amber-500/40 bg-amber-500/10 text-amber-400'
                          : 'border-border bg-muted/40 text-muted-foreground',
                      )}
                    >
                      {DEVICE_LABELS[type]}{count > 1 ? ` \u00d7${count}` : ''}
                    </span>
                  );
                })}
                {!Object.values(targetUnit.linked_devices ?? {}).some(Boolean) && (
                  <span className="text-[10px] text-muted-foreground/60">No devices linked yet</span>
                )}
              </div>
            </div>
          ) : willCreate ? (
            <div className="rounded-lg border border-[#8FB560]/40 bg-[#8FB560]/[0.04] px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-mono text-base font-semibold">{searchCode}</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8FB560]">Will be created</span>
              </div>
              <div className="flex items-center justify-between gap-3 mt-2 text-xs">
                <span className="text-muted-foreground">
                  Model {deriveModelCode(searchCode, knownModels) ?? '\u2014'}
                </span>
                {companies.length > 1 ? (
                  <select
                    aria-label="Company for the new unit"
                    value={companyId}
                    onChange={e => setCompanyId(e.target.value)}
                    className="h-7 rounded bg-background border border-border px-1.5 text-xs max-w-[55%]"
                  >
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <span className="text-muted-foreground truncate">{companies[0]?.name ?? '\u2014'}</span>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border px-4 py-5 text-sm text-muted-foreground text-center">
              Search for the unit to link to.
            </div>
          )}

          {!showSearch && (
            <button
              type="button"
              onClick={() => { setShowSearch(true); setSearch(''); setSelectedUnit(null); }}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
            >
              Choose a different unit
            </button>
          )}
        </section>

        {showSearch && (
          <section className="space-y-2">
            <div className="relative">
              <SearchInput
                placeholder="Search units by code..."
                value={search}
                onValueChange={setSearch}
                className="bg-background border-border"
                autoFocus
              />
            </div>

            <div className="space-y-1 max-h-[260px] overflow-y-auto">
              {isSearching ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : units.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  {search.trim().length >= 2 ? 'No units found' : 'Type to search units'}
                </div>
              ) : (
                units.map((unit) => (
                  <button
                    key={unit.id}
                    onClick={() => { setSelectedUnit(unit); setShowSearch(false); }}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors bg-muted/30 hover:bg-muted/50 border border-transparent"
                  >
                    <span className="font-mono text-sm font-medium shrink-0">{unit.unit_code}</span>
                    <span className="flex items-center gap-1.5 min-w-0">
                      {(Object.keys(DEVICE_LABELS) as DeviceType[]).map(type => {
                        const count = unit.linked_devices?.[type] ?? 0;
                        if (!count) return null;
                        return (
                          <span
                            key={type}
                            className={cn(
                              'text-[10px] px-1.5 py-0.5 rounded border',
                              type === deviceType
                                ? 'border-amber-500/40 bg-amber-500/10 text-amber-400'
                                : 'border-border bg-muted/40 text-muted-foreground',
                            )}
                          >
                            {DEVICE_LABELS[type]}{count > 1 ? ` \u00d7${count}` : ''}
                          </span>
                        );
                      })}
                    </span>
                    {unit.model_code && (
                      <span className="text-xs text-muted-foreground ml-auto shrink-0">{unit.model_code}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </section>
        )}

        {/* Other unlinked devices wearing the same unit name. Linking them in
            the same pass is the whole point: they arrived together. */}
        {alsoLinkable.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
              Also link
            </h3>
            <div className="space-y-1">
              {alsoLinkable.map(d => (
                <label
                  key={d.id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!alsoSelected[d.id]}
                    onChange={e => setAlsoSelected(prev => ({ ...prev, [d.id]: e.target.checked }))}
                    className="accent-[#8FB560]"
                  />
                  <span className="text-xs w-16 shrink-0">{DEVICE_LABELS[d.type]}</span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground/50 shrink-0 w-24">
                    {d.serialLabel}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground truncate">{d.serial}</span>
                </label>
              ))}
            </div>
          </section>
        )}

        {isSwap && (
          <section className="rounded-lg border border-amber-500/30 bg-amber-500/[0.04] p-3 space-y-2">
            <p className="text-xs text-amber-400">
              {targetOccupied
                ? `${targetUnit?.unit_code} already has a ${DEVICE_LABELS[deviceType]} device. Linking this one replaces it.`
                : `This device is linked to ${currentUnitCode}. Linking moves it to ${targetUnit?.unit_code}.`}
            </p>
            <textarea
              aria-label="Reason for replacing the existing link"
              value={reason}
              onChange={e => { setReason(e.target.value); setLinkError(null); }}
              placeholder="Reason (recorded in the audit log)"
              rows={2}
              className="w-full rounded-md bg-background border border-border p-2 text-sm resize-none"
            />
          </section>
        )}

        {(linkError || createError) && (
          <p className="text-xs text-red-400">{linkError || createError}</p>
        )}

        <div className="flex gap-2 pt-2 border-t border-border">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            onClick={handlePrimary}
            disabled={(!targetUnit && !willCreate) || isSubmitting || isCreating || (willCreate && !companyId)}
            className="flex-1 gap-2 bg-[#8FB560] hover:bg-[#8FB560]/90 text-black"
          >
            {(isSubmitting || isCreating) ? <Loader2 className="h-4 w-4 animate-spin" /> : willCreate ? <Plus className="h-4 w-4" /> : null}
            {primaryLabel}
          </Button>
        </div>
      </div>
    </AdminFormDrawer>
  );
}
